import { MatrixAxis, MatrixTable } from './table';

function normalizeLookupCode(raw: string | undefined): string {
    return String(raw ?? '').trim().toLowerCase();
}

/** True when saved lookup bands match question choice values in order (case-insensitive codes). */
export function matrixAxisLookupMatchesChoices(
    savedLookupValues: string[] | undefined,
    choiceValues: string[] | undefined,
): boolean {
    const saved = savedLookupValues ?? [];
    const choices = choiceValues ?? [];
    if (saved.length !== choices.length) {
        return false;
    }
    return saved.every((code, i) => normalizeLookupCode(code) === normalizeLookupCode(choices[i]));
}

/** Warning when a Code-question axis has saved lookup bands that no longer match its choices. */
export function matrixAxisLookupDriftMessage(
    axisLabel: 'Rows' | 'Columns',
    axis: MatrixAxis,
    choiceValues: string[] | undefined,
): string | null {
    if (axis.breakpoints?.length || !axis.lookupValues?.length || choiceValues === undefined) {
        return null;
    }
    if (matrixAxisLookupMatchesChoices(axis.lookupValues, choiceValues)) {
        return null;
    }
    return `${axisLabel}: saved lookup codes no longer match this question's choices. Use Change row & column inputs, then Apply, to rebuild the grid.`;
}

/** Rebuild lookup bands from question choices (Apply row/column inputs). */
export function syncMatrixAxisCodeLookupBands(axis: MatrixAxis, choiceValues: string[]): void {
    axis.breakpoints = undefined;
    axis.lookupValues = [...choiceValues];
}

/** Band count for one axis (breakpoints or lookup list length). */
export function matrixAxisBinCount(axis: MatrixAxis): number {
    if (axis.breakpoints && axis.breakpoints.length > 0) {
        return axis.breakpoints.length;
    }
    if (axis.lookupValues && axis.lookupValues.length > 0) {
        return axis.lookupValues.length;
    }
    return 0;
}

function validateAxis(label: string, axis: MatrixAxis): string[] {
    const errs: string[] = [];
    const breakpoints = axis.breakpoints;
    const lookupValues = axis.lookupValues;
    const hasBp = !!(breakpoints && breakpoints.length > 0);
    const hasLk = !!(lookupValues && lookupValues.length > 0);

    if (hasBp && hasLk) {
        errs.push(`${label}: choose either numeric breakpoints or lookup codes, not both.`);
    }
    if (!hasBp && !hasLk) {
        errs.push(`${label}: add at least one breakpoint or lookup code.`);
    }
    if (!axis.keyId && !axis.formulaId) {
        errs.push(`${label}: select a question, calculation key, or formula as input.`);
    }

    if (hasBp && breakpoints) {
        for (let i = 0; i < breakpoints.length - 1; i++) {
            if (!(breakpoints[i] < breakpoints[i + 1]) || !Number.isFinite(breakpoints[i]) || !Number.isFinite(breakpoints[i + 1])) {
                errs.push(`${label}: breakpoints must be finite and strictly ascending (position ${i}).`);
            }
        }
        if (breakpoints.length === 1 && !Number.isFinite(breakpoints[0])) {
            errs.push(`${label}: breakpoint must be a finite number.`);
        }
    }

    if (hasLk && lookupValues) {
        const seen = new Set<string>();
        for (let i = 0; i < lookupValues.length; i++) {
            const raw = lookupValues[i];
            const code = String(raw ?? '').trim();
            if (!code) {
                errs.push(`${label}: lookup code at position ${i + 1} is empty.`);
                continue;
            }
            const k = code.toLowerCase();
            if (seen.has(k)) {
                errs.push(`${label}: duplicate lookup code "${code}".`);
            }
            seen.add(k);
        }
    }

    return errs;
}

/**
 * Validates a matrix table for save. Empty array means OK.
 */
export function validateMatrixTable(mt: MatrixTable): string[] {
    const errs: string[] = [...validateAxis('Rows', mt.rows), ...validateAxis('Columns', mt.columns)];

    const nCols = matrixAxisBinCount(mt.columns);
    const nRows = matrixAxisBinCount(mt.rows);
    if (!nCols || !nRows) {
        return errs;
    }

    const expected = nRows * nCols;
    if (!mt.values || !Array.isArray(mt.values)) {
        errs.push(`Values grid: expected a flat array of ${expected} number(s).`);
        return errs;
    }
    if (mt.values.length !== expected) {
        errs.push(`Values grid: expected ${expected} cell(s) (${nRows} rows × ${nCols} columns), found ${mt.values.length}.`);
        return errs;
    }

    for (let i = 0; i < expected; i++) {
        const v = mt.values[i];
        if (typeof v !== 'number' || !Number.isFinite(v)) {
            const row = Math.floor(i / nCols) + 1;
            const col = (i % nCols) + 1;
            errs.push(`Values grid: cell [${row}, ${col}] must be a finite number.`);
        }
    }

    return errs;
}
